import {
  type Expr,
  num,
  variable,
  add,
  mul,
  pow,
  func,
  equals,
  canonicalise,
  nodeAtPath,
  replaceAtPath,
} from './expr';
import { Fraction } from '@learnforge/demo-math/numeric';
import { MultiPolynomial } from './multipoly';

export type RuleName =
  | 'distribute'
  | 'collect-like-terms'
  | 'factor-common-monomial'
  | 'cancel-common-factor'
  | 'simplify-fraction'
  | 'flatten-add'
  | 'flatten-mul'
  | 'exponent-product'
  | 'exponent-power'
  | 'exponent-quotient'
  | 'negative-exponent'
  | 'zero-exponent'
  | 'log-product'
  | 'log-power'
  | 'log-quotient'
  | 'change-of-base'
  | 'radical-to-frac-exp'
  | 'frac-exp-to-radical'
  | 'rationalize-denominator'
  | 'multiply-by-conjugate';

export interface RewriteStep {
  rule: RuleName;
  before: Expr;
  after: Expr;
  path: readonly number[];
  description: string;
}

type RuleFn = (e: Expr) => Expr | null;

// Rule implementations
const distribute: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'mul') return null;

  // Find the first add node
  let addIdx = -1;
  for (let i = 0; i < e.children.length; i += 1) {
    if (e.children[i]!.kind === 'add') {
      addIdx = i;
      break;
    }
  }

  if (addIdx === -1) return null;

  const addNode = e.children[addIdx]!;
  if (addNode.kind !== 'add') return null;

  // Collect all other children
  const otherChildren: Expr[] = [];
  for (let i = 0; i < e.children.length; i += 1) {
    if (i !== addIdx) {
      otherChildren.push(e.children[i]!);
    }
  }

  // If there are no other children, can't distribute
  if (otherChildren.length === 0) return null;

  // Distribute: (a + b + ...) * c * d * ... = (a*c*d*...) + (b*c*d*...) + ...
  const scalar = otherChildren.length === 1 ? otherChildren[0]! : mul(...otherChildren);
  const distributed = addNode.children.map((term) => mul(scalar, term));
  return add(...distributed);
};

const collectLikeTerms: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'add') return null;
  const groups = new Map<string, { key: string; terms: Expr[]; coeff: Fraction }>();
  let hasNumeric = false;
  let numericSum = Fraction.ZERO;

  for (const child of e.children) {
    if (child.kind === 'num') {
      numericSum = numericSum.add(child.value);
      hasNumeric = true;
      continue;
    }

    let coeff = Fraction.ONE;
    let nonCoeff: Expr = child;

    // Extract numeric coefficient from mul
    if (child.kind === 'mul' && child.children.length > 0 && child.children[0]!.kind === 'num') {
      const firstChild = child.children[0];
      if (firstChild && firstChild.kind === 'num') {
        coeff = firstChild.value;
        const rest = child.children.slice(1);
        nonCoeff = rest.length === 1 ? rest[0]! : mul(...rest);
      }
    }

    const key = structuralHashKey(nonCoeff);
    const group = groups.get(key);
    if (group) {
      group.coeff = group.coeff.add(coeff);
    } else {
      groups.set(key, { key, terms: [child], coeff });
    }
  }

  // Check if anything combined
  const totalGroups = groups.size + (hasNumeric && numericSum.num !== 0 ? 1 : 0);
  if (totalGroups === e.children.length) {
    return null;
  }

  const result: Expr[] = [];
  if (hasNumeric && numericSum.num !== 0) {
    result.push(num(numericSum));
  }

  for (const { coeff, key } of groups.values()) {
    const groupItem = groups.get(key);
    if (!groupItem) continue;
    const firstTerm = groupItem.terms[0];
    if (!firstTerm) continue;

    let nonCoeff: Expr = firstTerm;
    if (
      firstTerm.kind === 'mul' &&
      firstTerm.children.length > 0 &&
      firstTerm.children[0]!.kind === 'num'
    ) {
      const rest = firstTerm.children.slice(1);
      nonCoeff = rest.length === 1 ? rest[0]! : mul(...rest);
    }

    if (coeff.equals(Fraction.ONE)) {
      result.push(nonCoeff);
    } else {
      result.push(mul(num(coeff), nonCoeff));
    }
  }

  if (result.length === 0) return num(0);
  if (result.length === 1) return result[0]!;
  return add(...result);
};

const factorCommonMonomial: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'add') return null;

  // First pass: extract coefficients and track variable powers per term
  const terms: { coeff: Fraction; varPowers: Map<string, number> }[] = [];
  let gcdCoeff = Fraction.ZERO;

  for (const child of e.children) {
    let coeff = Fraction.ONE;
    let remaining = child;

    if (child.kind === 'num') {
      coeff = child.value;
      gcdCoeff = gcdCoeff.num === 0 ? coeff : gcdFromFractions(gcdCoeff, coeff);
      terms.push({ coeff, varPowers: new Map() });
      continue;
    }

    if (child.kind === 'mul') {
      const firstChild = child.children[0];
      if (firstChild && firstChild.kind === 'num') {
        coeff = firstChild.value;
        const rest = child.children.slice(1);
        remaining = rest.length === 1 ? rest[0]! : mul(...rest);
      }
    }

    gcdCoeff = gcdCoeff.num === 0 ? coeff : gcdFromFractions(gcdCoeff, coeff);

    const varPowers = new Map<string, number>();
    extractVarPowers(remaining, varPowers);
    terms.push({ coeff, varPowers });
  }

  // Find common variables (appear in ALL terms with same min power)
  const commonVars = new Map<string, number>();
  if (terms.length > 0 && terms[0]) {
    for (const [varName, power] of terms[0].varPowers) {
      let minPower = power;
      let isCommon = true;

      for (let i = 1; i < terms.length; i += 1) {
        const termVarPower = terms[i]!.varPowers.get(varName);
        if (termVarPower === undefined) {
          isCommon = false;
          break;
        }
        minPower = Math.min(minPower, termVarPower);
      }

      if (isCommon && minPower > 0) {
        commonVars.set(varName, minPower);
      }
    }
  }

  // Check if we have a non-trivial GCD or common variables
  const hasCommonCoeff =
    gcdCoeff.num !== 0 && !gcdCoeff.equals(Fraction.ONE) && !gcdCoeff.equals(Fraction.ONE.neg());
  const hasCommonVar = commonVars.size > 0;

  if (!hasCommonCoeff && !hasCommonVar) {
    return null;
  }

  // Build the GCF
  let gcf: Expr | null = null;
  if (hasCommonCoeff) {
    gcf = num(gcdCoeff);
  }

  for (const [varName, minPower] of commonVars) {
    const varTerm = minPower === 1 ? variable(varName) : pow(variable(varName), num(minPower));
    gcf = gcf ? mul(gcf, varTerm) : varTerm;
  }

  if (!gcf) {
    return null;
  }

  // Factor out from each term
  const reduced: Expr[] = [];
  for (const { coeff, varPowers } of terms) {
    let newCoeff = coeff;
    if (hasCommonCoeff) {
      newCoeff = newCoeff.div(gcdCoeff);
    }

    const remainingPowers = new Map(varPowers);
    for (const [varName] of commonVars) {
      const power = remainingPowers.get(varName) ?? 0;
      if (power > 0) {
        remainingPowers.set(varName, power - (commonVars.get(varName) ?? 0));
        if (remainingPowers.get(varName) === 0) {
          remainingPowers.delete(varName);
        }
      }
    }

    let term: Expr = newCoeff.equals(Fraction.ONE) ? num(1) : num(newCoeff);
    if (remainingPowers.size > 0) {
      const varTerms: Expr[] = [];
      for (const [varName, power] of remainingPowers) {
        if (power === 1) {
          varTerms.push(variable(varName));
        } else {
          varTerms.push(pow(variable(varName), num(power)));
        }
      }
      const varPart = varTerms.length === 1 ? varTerms[0]! : mul(...varTerms);
      term = newCoeff.equals(Fraction.ONE) ? varPart : mul(num(newCoeff), varPart);
    }

    reduced.push(term);
  }

  const reducedAdded = reduced.length === 1 ? reduced[0]! : add(...reduced);
  return mul(gcf, reducedAdded);
};

const cancelCommonFactor: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'mul') return null;

  let numerExpr: Expr | null = null;
  const denomPowers: { base: Expr; exp: Expr }[] = [];

  for (const child of e.children) {
    if (child.kind === 'pow' && child.exp.kind === 'num') {
      const expVal = child.exp as { kind: 'num'; value: Fraction };
      if (expVal.value.num < 0) {
        denomPowers.push({ base: child.base, exp: child.exp });
      } else {
        numerExpr = numerExpr ? mul(numerExpr, child) : child;
      }
    } else {
      numerExpr = numerExpr ? mul(numerExpr, child) : child;
    }
  }

  if (!numerExpr || denomPowers.length === 0) {
    return null;
  }

  // Try to factor the numerator to find common factors
  let changed = false;

  if (numerExpr.kind === 'add') {
    // Check if all terms in the add have a common factor matching a denom base
    for (const denomPow of denomPowers) {
      const base = denomPow.base;
      if (numerExpr.kind === 'add') {
        const factorResult = tryFactorFromAdd(numerExpr, base);
        if (factorResult) {
          numerExpr = factorResult;
          changed = true;
          break;
        }
      }
    }
  }

  if (!changed) {
    return null;
  }

  const result: Expr[] = [];
  if (numerExpr.kind !== 'num' || numerExpr.value.num !== 1) {
    result.push(numerExpr);
  }
  result.push(...denomPowers.map((dp) => pow(dp.base, dp.exp)));

  if (result.length === 0) return num(1);
  if (result.length === 1) return result[0]!;
  return mul(...result);
};

const simplifyFraction: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'mul') return null;

  let numValue: Fraction | null = null;
  let denomValue: Fraction | null = null;
  const otherChildren: Expr[] = [];

  for (const child of e.children) {
    if (child.kind === 'num') {
      numValue = child.value;
    } else if (child.kind === 'pow' && child.exp.kind === 'num' && child.base.kind === 'num') {
      const expVal = child.exp as { kind: 'num'; value: Fraction };
      const baseVal = child.base as { kind: 'num'; value: Fraction };
      if (expVal.value.num === -1) {
        denomValue = baseVal.value;
      } else {
        otherChildren.push(child);
      }
    } else {
      otherChildren.push(child);
    }
  }

  if (numValue && denomValue) {
    const result = numValue.div(denomValue);
    if (otherChildren.length === 0) {
      return num(result);
    }
    return mul(num(result), ...otherChildren);
  }

  return null;
};

const flattenAdd: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'add') return null;

  const flattened: Expr[] = [];
  let changed = false;

  for (const child of e.children) {
    if (child.kind === 'add') {
      flattened.push(...child.children);
      changed = true;
    } else {
      flattened.push(child);
    }
  }

  if (!changed) {
    return null;
  }

  if (flattened.length === 1) return flattened[0]!;
  return add(...flattened);
};

const flattenMul: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'mul') return null;

  const flattened: Expr[] = [];
  let changed = false;

  for (const child of e.children) {
    if (child.kind === 'mul') {
      flattened.push(...child.children);
      changed = true;
    } else {
      flattened.push(child);
    }
  }

  if (!changed) {
    return null;
  }

  if (flattened.length === 1) return flattened[0]!;
  return mul(...flattened);
};

const exponentProduct: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'mul') return null;

  const powByBase = new Map<string, { base: Expr; exps: Expr[] }>();

  for (const child of e.children) {
    if (child.kind === 'pow') {
      const baseKey = structuralHashKey(child.base);
      const group = powByBase.get(baseKey);
      if (group) {
        group.exps.push(child.exp);
      } else {
        powByBase.set(baseKey, { base: child.base, exps: [child.exp] });
      }
    } else {
      // Treat as pow(..., 1)
      const baseKey = structuralHashKey(child);
      const group = powByBase.get(baseKey);
      if (group) {
        group.exps.push(num(1));
      } else {
        powByBase.set(baseKey, { base: child, exps: [num(1)] });
      }
    }
  }

  let changed = false;
  for (const group of powByBase.values()) {
    if (group.exps.length > 1) {
      changed = true;
      break;
    }
  }

  if (!changed) {
    return null;
  }

  const result: Expr[] = [];
  for (const { base, exps } of powByBase.values()) {
    if (exps.length === 1) {
      const exp = exps[0];
      if (exp && exp.kind === 'num' && exp.value.equals(Fraction.ONE)) {
        result.push(base);
      } else {
        result.push(pow(base, exp!));
      }
    } else {
      // Sum ALL exponents cleanly
      let sumExp: Expr = exps[0]!;
      for (let i = 1; i < exps.length; i += 1) {
        sumExp = add(sumExp, exps[i]!);
      }
      result.push(pow(base, sumExp));
    }
  }

  if (result.length === 1) return result[0]!;
  return mul(...result);
};

const exponentPower: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'pow') return null;
  if (e.base.kind !== 'pow') return null;

  const base = e.base;
  const innerBase = base.base;
  const m = base.exp;
  const n = e.exp;

  const product = mul(m, n);
  return pow(innerBase, product);
};

const exponentQuotient: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'mul') return null;

  const powByBase = new Map<string, { base: Expr; exps: Expr[] }>();
  const otherChildren: Expr[] = [];

  for (const child of e.children) {
    if (child.kind === 'pow') {
      const baseKey = structuralHashKey(child.base);
      const group = powByBase.get(baseKey);
      if (group) {
        group.exps.push(child.exp);
      } else {
        powByBase.set(baseKey, { base: child.base, exps: [child.exp] });
      }
    } else {
      otherChildren.push(child);
    }
  }

  let changed = false;
  for (const group of powByBase.values()) {
    if (group.exps.length > 1) {
      changed = true;
      break;
    }
  }

  if (!changed) {
    return null;
  }

  const result: Expr[] = [...otherChildren];
  for (const { base, exps } of powByBase.values()) {
    if (exps.length === 1) {
      const exp = exps[0];
      if (exp && exp.kind === 'num' && exp.value.equals(Fraction.ONE)) {
        result.push(base);
      } else {
        result.push(pow(base, exp || num(1)));
      }
    } else {
      const sumExp = exps.reduce((acc: Expr, e: Expr) => add(acc, e));
      result.push(pow(base, sumExp));
    }
  }

  if (result.length === 0) return num(1);
  if (result.length === 1) return result[0]!;
  return mul(...result);
};

const negativeExponent: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'pow') return null;
  if (e.exp.kind !== 'num') return null;

  const expVal = e.exp.value;
  if (expVal.num >= 0) return null;

  // x^(-n) → 1 · (x^n)^(-1) where n = |exponent|
  const n = Math.abs(expVal.num);
  const posExp = num(new Fraction(n, expVal.den));
  const positivePolyBase = pow(e.base, posExp);
  return mul(num(1), pow(positivePolyBase, num(-1)));
};

const zeroExponent: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'pow') return null;
  if (e.exp.kind !== 'num') return null;

  const expVal = e.exp.value;
  if (expVal.num !== 0) return null;

  return num(1);
};

const logProduct: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'func') return null;
  if (!['log', 'ln', 'log2', 'log10'].includes(e.name)) return null;
  if (e.args.length !== 1) return null;

  const arg = e.args[0];
  if (!arg || arg.kind !== 'mul') return null;

  const terms = arg.children.map((child) => func(e.name, child));
  return add(...terms);
};

const logPower: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'func') return null;
  if (!['log', 'ln', 'log2', 'log10'].includes(e.name)) return null;
  if (e.args.length !== 1) return null;

  const arg = e.args[0];
  if (!arg || arg.kind !== 'pow') return null;

  return mul(arg.exp, func(e.name, arg.base));
};

const logQuotient: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'func') return null;
  if (!['log', 'ln', 'log2', 'log10'].includes(e.name)) return null;
  if (e.args.length !== 1) return null;

  const arg = e.args[0];
  if (!arg || arg.kind !== 'mul') return null;

  let numerator: Expr | null = null;
  let denominator: Expr | null = null;

  for (const child of arg.children) {
    if (child.kind === 'pow' && child.exp.kind === 'num') {
      const expVal = child.exp.value;
      if (expVal.num < 0) {
        denominator = child.base;
      } else {
        numerator = numerator ? mul(numerator, child) : child;
      }
    } else {
      numerator = numerator ? mul(numerator, child) : child;
    }
  }

  if (!numerator || !denominator) {
    return null;
  }

  return add(func(e.name, numerator), mul(num(-1), func(e.name, denominator)));
};

const changeOfBase: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'func') return null;

  if (e.name === 'log2') {
    if (e.args.length !== 1) return null;
    const arg = e.args[0];
    // log2(x) = log10(x) / log10(2)
    return mul(func('log10', arg!), pow(func('log10', num(2)), num(-1)));
  }

  return null;
};

const radicalToFracExp: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'func') return null;
  if (e.args.length !== 1) return null;

  const arg = e.args[0];
  if (!arg) return null;

  if (e.name === 'sqrt') {
    return pow(arg, num(new Fraction(1, 2)));
  }
  if (e.name === 'cbrt') {
    return pow(arg, num(new Fraction(1, 3)));
  }

  return null;
};

const fracExpToRadical: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'pow') return null;
  if (e.exp.kind !== 'num') return null;

  const expVal = e.exp.value;

  if (expVal.num === 1 && expVal.den === 2) {
    return func('sqrt', e.base);
  }
  if (expVal.num === 1 && expVal.den === 3) {
    return func('cbrt', e.base);
  }

  return null;
};

const multiplyByConjugate: RuleFn = (e: Expr): Expr | null => {
  // Pattern: a / (b + c) where b or c contains a radical
  if (e.kind !== 'mul') return null;

  let numerator: Expr | null = null;
  let denominator: Expr | null = null;

  for (const child of e.children) {
    if (child.kind === 'pow' && child.exp.kind === 'num') {
      const expVal = child.exp.value;
      if (expVal.num === -1) {
        denominator = child.base;
      } else {
        numerator = numerator ? mul(numerator, child) : child;
      }
    } else {
      numerator = numerator ? mul(numerator, child) : child;
    }
  }

  if (!numerator || !denominator || denominator.kind !== 'add') {
    return null;
  }

  // Check if denominator has radical
  if (!exprContainsRadical(denominator)) {
    return null;
  }

  // Denominator is a + b (or more), conjugate is a - b
  if (denominator.children.length !== 2) {
    return null;
  }

  const a = denominator.children[0];
  const b = denominator.children[1];
  if (!a || !b) return null;

  const conjugate = add(a, mul(num(-1), b));
  const newNumerator = mul(numerator, conjugate);
  const newDenominator = mul(denominator, conjugate);

  return mul(newNumerator, pow(newDenominator, num(-1)));
};

const rationalizeDenominator: RuleFn = (e: Expr): Expr | null => {
  if (e.kind !== 'mul') return null;

  let numerator: Expr | null = null;
  let denominator: Expr | null = null;

  for (const child of e.children) {
    if (child.kind === 'pow' && child.exp.kind === 'num') {
      const expVal = child.exp.value;
      if (expVal.num === -1) {
        denominator = child.base;
      } else {
        numerator = numerator ? mul(numerator, child) : child;
      }
    } else {
      numerator = numerator ? mul(numerator, child) : child;
    }
  }

  if (!numerator || !denominator || denominator.kind !== 'add') {
    return null;
  }

  // Check if denominator has exactly 2 terms and one is a radical
  if (denominator.children.length !== 2) {
    return null;
  }

  const a = denominator.children[0];
  const b = denominator.children[1];
  if (!a || !b) return null;

  // Conjugate is a - b
  const conjugate = add(a, mul(num(-1), b));

  // Multiply numerator and denominator by conjugate
  const newNumerator = mul(numerator, conjugate);
  const newDenominatorExpr = mul(denominator, conjugate);

  // Return as fraction (simplified)
  return mul(newNumerator, pow(newDenominatorExpr, num(-1)));
};

const ruleFunctions: Record<RuleName, RuleFn> = {
  distribute,
  'collect-like-terms': collectLikeTerms,
  'factor-common-monomial': factorCommonMonomial,
  'cancel-common-factor': cancelCommonFactor,
  'simplify-fraction': simplifyFraction,
  'flatten-add': flattenAdd,
  'flatten-mul': flattenMul,
  'exponent-product': exponentProduct,
  'exponent-power': exponentPower,
  'exponent-quotient': exponentQuotient,
  'negative-exponent': negativeExponent,
  'zero-exponent': zeroExponent,
  'log-product': logProduct,
  'log-power': logPower,
  'log-quotient': logQuotient,
  'change-of-base': changeOfBase,
  'radical-to-frac-exp': radicalToFracExp,
  'frac-exp-to-radical': fracExpToRadical,
  'multiply-by-conjugate': multiplyByConjugate,
  'rationalize-denominator': rationalizeDenominator,
};

export function applyRule(expr: Expr, rule: RuleName, path: readonly number[] = []): Expr | null {
  const ruleFn = ruleFunctions[rule];
  if (!ruleFn) {
    throw new Error(`Unknown rule: ${rule}`);
  }

  const node = nodeAtPath(expr, path);
  if (!node) {
    return null;
  }

  const rewritten = ruleFn(node);
  if (!rewritten) {
    return null;
  }

  if (path.length === 0) {
    return rewritten;
  }

  return replaceAtPath(expr, path, rewritten);
}

export function applicableSites(expr: Expr, rule: RuleName): readonly (readonly number[])[] {
  const ruleFn = ruleFunctions[rule];
  if (!ruleFn) {
    throw new Error(`Unknown rule: ${rule}`);
  }

  const sites: (readonly number[])[] = [];

  const traverse = (e: Expr, path: readonly number[]): void => {
    if (ruleFn(e)) {
      sites.push(path);
    }

    switch (e.kind) {
      case 'add':
      case 'mul': {
        for (let i = 0; i < e.children.length; i += 1) {
          traverse(e.children[i]!, [...path, i]);
        }
        break;
      }
      case 'pow': {
        traverse(e.base, [...path, 0]);
        traverse(e.exp, [...path, 1]);
        break;
      }
      case 'func': {
        for (let i = 0; i < e.args.length; i += 1) {
          traverse(e.args[i]!, [...path, i]);
        }
        break;
      }
      case 'sigma': {
        traverse(e.from, [...path, 0]);
        traverse(e.to, [...path, 1]);
        traverse(e.body, [...path, 2]);
        break;
      }
      case 'num':
      case 'var':
        break;
    }
  };

  traverse(expr, []);
  return sites;
}

function countNodes(e: Expr): number {
  switch (e.kind) {
    case 'num':
    case 'var':
      return 1;
    case 'add':
    case 'mul':
      return 1 + e.children.reduce((s, c) => s + countNodes(c), 0);
    case 'pow':
      return 1 + countNodes(e.base) + countNodes(e.exp);
    case 'func':
      return 1 + e.args.reduce((s, a) => s + countNodes(a), 0);
    case 'sigma':
      return 1 + countNodes(e.from) + countNodes(e.to) + countNodes(e.body);
  }
}

export function autoSimplify(expr: Expr): { result: Expr; steps: RewriteStep[] } {
  const steps: RewriteStep[] = [];
  let current = expr;
  const maxIterations = 100;
  let iterationCount = 0;

  const pipeline: RuleName[] = [
    'flatten-add',
    'flatten-mul',
    'simplify-fraction',
    'zero-exponent',
    'negative-exponent',
    'exponent-product',
    'exponent-power',
    'exponent-quotient',
    'collect-like-terms',
    'distribute',
    'cancel-common-factor',
    'log-product',
    'log-power',
    'log-quotient',
  ];

  while (iterationCount < maxIterations) {
    let changed = false;

    for (const rule of pipeline) {
      const sites = applicableSites(current, rule);

      for (const path of sites) {
        const before = current;
        const newExpr = applyRule(current, rule, path);

        if (newExpr && !equals(newExpr, current)) {
          // For distribute, only apply if it doesn't grow the tree too much
          if (rule === 'distribute') {
            const beforeCount = countNodes(before);
            const afterCount = countNodes(newExpr);
            if (afterCount > beforeCount * 2) {
              // Skip this application to avoid exponential growth
              continue;
            }
          }

          current = newExpr;
          current = canonicalise(current);

          steps.push({
            rule,
            before,
            after: current,
            path,
            description: `Applied rule: ${rule}`,
          });

          changed = true;
          break;
        }
      }

      if (changed) break;
    }

    if (!changed) break;
    iterationCount += 1;
  }

  return { result: current, steps };
}

export function equivalent(a: Expr, b: Expr): boolean {
  // Try fast polynomial path first
  const polyA = MultiPolynomial.fromExpr(a);
  const polyB = MultiPolynomial.fromExpr(b);

  if (polyA && polyB) {
    return polyA.equals(polyB);
  }

  // If only one is a polynomial, try to convert the non-polynomial by simplifying
  if (polyA && !polyB) {
    const simplifiedB = autoSimplify(b);
    const polyB2 = MultiPolynomial.fromExpr(simplifiedB.result);
    if (polyB2) {
      return polyA.equals(polyB2);
    }
  }

  if (polyB && !polyA) {
    const simplifiedA = autoSimplify(a);
    const polyA2 = MultiPolynomial.fromExpr(simplifiedA.result);
    if (polyA2) {
      return polyA2.equals(polyB);
    }
  }

  // Fallback: structural equality after canonicalizing
  const canA = canonicalise(a);
  const canB = canonicalise(b);

  return equals(canA, canB);
}

// Helper functions

function structuralHashKey(e: Expr): string {
  switch (e.kind) {
    case 'num':
      return `num(${e.value.toString()})`;
    case 'var':
      return `var(${e.name})`;
    case 'add': {
      const hashes = e.children
        .map((c) => structuralHashKey(c))
        .sort()
        .join('|');
      return `add(${hashes})`;
    }
    case 'mul': {
      const hashes = e.children
        .map((c) => structuralHashKey(c))
        .sort()
        .join('|');
      return `mul(${hashes})`;
    }
    case 'pow':
      return `pow(${structuralHashKey(e.base)},${structuralHashKey(e.exp)})`;
    case 'func': {
      const hashes = e.args.map((a) => structuralHashKey(a)).join('|');
      return `func(${e.name},${hashes})`;
    }
    case 'sigma':
      return `sigma(${e.index},${structuralHashKey(e.from)},${structuralHashKey(e.to)},${structuralHashKey(e.body)})`;
  }
}

function gcdFromFractions(a: Fraction, b: Fraction): Fraction {
  const gcdNum = gcdInt(Math.abs(a.num), Math.abs(b.num));
  const lcmDen = (a.den * b.den) / gcdInt(a.den, b.den);
  return new Fraction(gcdNum, lcmDen);
}

function gcdInt(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

function extractVarPowers(e: Expr, powers: Map<string, number>): void {
  switch (e.kind) {
    case 'var': {
      powers.set(e.name, (powers.get(e.name) ?? 0) + 1);
      break;
    }
    case 'pow': {
      if (e.base.kind === 'var' && e.exp.kind === 'num') {
        const power = e.exp.value.num;
        powers.set(e.base.name, (powers.get(e.base.name) ?? 0) + power);
      }
      break;
    }
    case 'mul': {
      for (const child of e.children) {
        extractVarPowers(child, powers);
      }
      break;
    }
  }
}

function exprContainsRadical(e: Expr): boolean {
  switch (e.kind) {
    case 'func':
      return ['sqrt', 'cbrt'].includes(e.name);
    case 'add':
    case 'mul':
      return e.children.some((c) => exprContainsRadical(c));
    case 'pow':
      return exprContainsRadical(e.base) || exprContainsRadical(e.exp);
    case 'sigma':
      return (
        exprContainsRadical(e.from) || exprContainsRadical(e.to) || exprContainsRadical(e.body)
      );
    default:
      return false;
  }
}

function tryFactorFromAdd(addExpr: Expr, base: Expr): Expr | null {
  if (addExpr.kind !== 'add') return null;

  // Try to factor out the base from all terms
  const newTerms: Expr[] = [];

  for (const term of addExpr.children) {
    if (term.kind === 'mul') {
      let found = false;
      const remaining: Expr[] = [];

      for (const child of term.children) {
        if (equals(child, base)) {
          found = true;
        } else {
          remaining.push(child);
        }
      }

      if (found) {
        const reduced =
          remaining.length === 0
            ? num(1)
            : remaining.length === 1
              ? remaining[0]!
              : mul(...remaining);
        newTerms.push(reduced);
      } else {
        return null;
      }
    } else if (equals(term, base)) {
      newTerms.push(num(1));
    } else {
      return null;
    }
  }

  if (newTerms.length === addExpr.children.length) {
    const reduced = newTerms.length === 1 ? newTerms[0]! : add(...newTerms);
    return mul(base, reduced);
  }

  return null;
}
